// action creators for both conversation and conversations
// get conversation from firestore, create (start) conversation for a prompt
// get conversations from firestore
import {
  doc,
  getDoc,
  addDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  onSnapshot,
  serverTimestamp,
  runTransaction
} from 'firebase/firestore';
import { firestore } from '../firebase';
import {
  asyncActionStart,
  asyncActionFinish,
  asyncActionError
} from './asyncActions';
import {
  GET_CONVERSATION,
  REMOVE_CONVERSATION_REDUX_STATE,
  UPDATE_CONVERSATIONS_REDUX_STATE,
  REMOVE_CONVERSATIONS_REDUX_STATE
} from './types';
// import mergeSortedConvosArrays from '../utils/mergeSortedConvosArrays';
import { getConversationsMessages } from './messagesActions';
import { openSnackbar } from './snackbarActions';
import { dispatchFirebaseError } from './errorsActions';

// create the a new conversation doc based on prompt and the auth'd user
// dispatch the new conversationID as the conversationObject
// we return conversationID so PromptPage.js can redirect user to the conversation
export const createConversation = promptObject => async (
  dispatch,
  getState
) => {
  dispatch(asyncActionStart());
  try {
    const {
      promptID,
      title: promptTitle,
      creator: promptCreator,
      creatorID: promptCreatorID,
      text: promptText,
      creatorDeleted
    } = promptObject;

    // check if the creator of the prompt is deleted / creatorDeleted is true
    // if creatorDeleted is true, do not create the conversation, send a error snackbar instead
    if (creatorDeleted) {
      const message = "The prompt creator's account has been deleted!";
      dispatch(
        openSnackbar('ErrorSnackbar', {
          message,
          autoHideDuration: 12000
        })
      );
      dispatch(asyncActionError());
      return null;
    }

    const currentUserID = getState().auth.currentUser.uid;
    const currentUserDisplayname = getState().auth.currentUser.displayName;
    const participants = [promptCreator, currentUserDisplayname];
    const participantsIDs = [promptCreatorID, currentUserID];
    const conversationsCollectionRef = collection(firestore, 'conversations');
    const result = await addDoc(conversationsCollectionRef, {
      promptID,
      promptTitle,
      conversationStarter: currentUserDisplayname,
      conversationStarterID: currentUserID,
      promptCreator,
      promptCreatorID,
      participants,
      participantsIDs,
      createdAt: serverTimestamp(),
      lastUpdated: serverTimestamp(),
      promptText,
      streak: 0, // number of current streaks
      // streakTimeLeft: serverTimestamp(), // time left to keep the streak going
      streakWaitingForUser: null // waiting for this user to keep the streak going
    });
    const conversationID = result.id;

    dispatch(asyncActionFinish());
    return conversationID;
  } catch (error) {
    console.error(error);
    dispatch(
      openSnackbar('ErrorSnackbar', {
        message: 'Could not start this conversation. Please try again.'
      })
    );
    dispatch(asyncActionError());
    return null;
  }
};

// get the conversation doc and also (this replaces getConversation() action creator)
// listen to the change in current conversation doc
// more precisely, listen to changes in the streak field
export const conversationListener = conversationID => async (
  dispatch,
  getState
) => {
  dispatch(asyncActionStart());
  try {
    const conversationRef = doc(firestore, 'conversations', conversationID);

    const unsubscribe = onSnapshot(
      conversationRef,
      docSnapshot => {
        // this catches the error if the doc DOES NOT EXIST
        // because we are in onSnapshot, we cannot catch a thrown error, that's
        // why we are dispatching error from here directly
        // we create a fake firebase permission error to match the error from below
        // to keep it uniform
        if (!docSnapshot.exists()) {
          const error = {
            code: 'permission-denied',
            message: 'Missing or insufficient permissions.'
          };
          dispatch(asyncActionError());
          dispatch(dispatchFirebaseError(error));
          return null;
        }
        const oldStreak = getState().conversation.streak;

        const newStreak = docSnapshot.data().streak;
        if (oldStreak !== newStreak) {
          const conversationData = docSnapshot.data();
          const conversationObject = {
            ...conversationData,
            conversationID,
            unsubscribeToConversationListener: unsubscribe,
            conversationListenerLoaded: true
          };
          dispatch({ type: GET_CONVERSATION, payload: { conversationObject } });
        }
        return true;
      },
      // this catches the error if the user doesn't have permission to view a doc
      // because we are in onSnapshot, we cannot catch a thrown error, that's
      // why we are dispatching error from here directly
      error => {
        dispatch(asyncActionError());
        dispatch(dispatchFirebaseError(error));
      }
    );
    dispatch(asyncActionFinish());
  } catch (error) {
    console.error(error);
  }
};

// check if the conversation with that prompt and the currently auth'd user exists
// if it exists then directly update the conversation redux state and go to the conversation page
// if it does Not exist, then call createConversation()
// after getting conversationID, return it back to PromptPage.js so PromptPage can
// redirect the user to the conversation page based on the conversationID
export const checkConversation = promptID => async (dispatch, getState) => {
  dispatch(asyncActionStart());
  try {
    const currentUserID = getState().auth.currentUser.uid;
    // check if there is already a conversation with that promptID and currentUserID
    const conversationQuery = query(
      collection(firestore, 'conversations'),
      where('promptID', '==', promptID),
      // where('conversationStarterID', '==', currentUserID), //this line doesn't work because of security rules
      where('participantsIDs', 'array-contains', currentUserID)
    );
    const conversationSnapshot = await getDocs(conversationQuery);
    // if conversationSnapshot is empty that means the conversation does Not exist
    // call the createConversation to make a new one
    if (conversationSnapshot.empty) {
      const promptRef = doc(firestore, 'prompts', promptID);
      const promptSnapshot = await getDoc(promptRef);
      const promptData = promptSnapshot.data();
      const promptObject = { ...promptData, promptID };
      return dispatch(createConversation(promptObject));
    }
    // otherwise, the conversation exists so we get the conversation and go to the page
    const conversationData = conversationSnapshot.docs[0].data();
    const conversationID = conversationSnapshot.docs[0].id;
    const conversationObject = { ...conversationData, conversationID };
    dispatch({ type: GET_CONVERSATION, payload: { conversationObject } });
    dispatch(asyncActionFinish());
    return conversationID;
  } catch (error) {
    console.error(error);
    dispatch(
      openSnackbar('ErrorSnackbar', {
        message: 'Could not open this conversation. Please try again.'
      })
    );
    dispatch(asyncActionError());
    return null;
  }
};

// //This is currently NOT used. We use conversationListener instead!
// // get the existing conversation from firestore based on the conversationID
// export const getConversation = conversationID => async dispatch => {
//   try {
//     const conversationRef = doc(firestore, 'conversations', conversationID);
//     const conversationSnapshot = await getDoc(conversationRef);
//     const conversationData = conversationSnapshot.data();
//     const conversationObject = { ...conversationData, conversationID };
//     dispatch({ type: GET_CONVERSATION, payload: { conversationObject } });
//   } catch (error) {
//     console.log(error);
//   }
// };

// resets the conversation redux state
// also unsubscribes from both conversationListener and conversationMessagesListener from state
export const removeConversationInState = () => async (dispatch, getState) => {
  try {
    const {
      unsubscribeToConversationListener,
      unsubscribeToMessagesListener,
      messagesListenerUnsubscribes
    } = getState().conversation;
    if (unsubscribeToConversationListener) {
      await unsubscribeToConversationListener();
    }
    // tear down EVERY messages listener opened for this conversation, not just
    // the most recent one (each "Load older messages" click opened another).
    if (
      Array.isArray(messagesListenerUnsubscribes) &&
      messagesListenerUnsubscribes.length > 0
    ) {
      messagesListenerUnsubscribes.forEach(unsub => {
        if (typeof unsub === 'function') {
          unsub();
        }
      });
    } else if (unsubscribeToMessagesListener) {
      await unsubscribeToMessagesListener();
    }

    dispatch({ type: REMOVE_CONVERSATION_REDUX_STATE });
  } catch (error) {
    console.error(error);
  }
};

export const getMyConversations = (searchConversationsObject = {}) => async (
  dispatch,
  getState
) => {
  dispatch(asyncActionStart());
  try {
    // // searchText and searchType are used for searching in SearchConversationsPage.js (/search_conversations/:searchID)
    // const { searchText, searchType, promptID } = searchConversationsObject;
    const { promptID } = searchConversationsObject;
    const userID = getState().auth.currentUser.uid;
    const currentMyConversations = getState().conversations.myConversations;

    // Build query constraints
    const queryConstraints = [
      where('participantsIDs', 'array-contains', userID),
      orderBy('lastUpdated', 'desc'),
      limit(10)
    ];

    // if promptID is passed in as an argument, get the conversations with that promptID
    if (promptID) {
      queryConstraints.push(where('promptID', '==', promptID));
    }

    // get the lastConversation from state, note that this is the
    // last conversation fetched from firestore and NOT the last conversation you've started
    // if it exists then find conversations after it,
    const currentLastConversation = getState().conversations.lastConversation;
    if (currentLastConversation) {
      queryConstraints.push(startAfter(currentLastConversation));
    }

    const myConversationsQuery = query(
      collection(firestore, 'conversations'),
      ...queryConstraints
    );

    const myConversationsSnapshot = await getDocs(myConversationsQuery);
    const myConversationsArray = myConversationsSnapshot.docs.map(docSnapshot => {
      const convoID = docSnapshot.id;
      const convoData = docSnapshot.data();
      return { ...convoData, conversationID: convoID };
    });

    // dispatch the action to get the messages for that conversation
    myConversationsArray.forEach(myConversation => {
      const { conversationID } = myConversation;
      dispatch(getConversationsMessages(conversationID, 'desc', 1));
    });

    // make myConversations into an object
    const preMyConversations = {};
    myConversationsArray.forEach(myConversation => {
      preMyConversations[myConversation.conversationID] = myConversation;
    });

    // merge the myConversations already in state with the newly fetched myConversations
    const myConversations = {
      ...currentMyConversations,
      ...preMyConversations
    };

    // get the lastConversation from the recently fetched conversations
    const lastConversation =
      myConversationsSnapshot.docs[myConversationsSnapshot.docs.length - 1];

    // see if there are any more conversations left in the firestore
    let moreConversations;
    if (myConversationsSnapshot.docs.length === 10) {
      moreConversations = true;
    } else {
      moreConversations = false;
    }

    dispatch({
      type: UPDATE_CONVERSATIONS_REDUX_STATE,
      payload: { myConversations, lastConversation, moreConversations }
    });
    dispatch(asyncActionFinish());
  } catch (error) {
    console.error(error);
    dispatch(asyncActionError());
  }
};

// export the testConversations in test_content/exampleConversations to firestore
// This works only when corresponding users and prompts are already in firestore
// used in testpage/TestPage.js
export const exportConversations = conversationObjects => async () => {
  dispatch(asyncActionStart());
  try {
    await Object.values(conversationObjects).forEach(async value => {
      const promptQuery = query(
        collection(firestore, 'prompts'),
        where('title', '==', value.promptTitle)
      );
      const promptResult = await getDocs(promptQuery);
      const prompt = promptResult.docs[0].data();
      const promptID = promptResult.docs[0].id;

      const userQuery = query(
        collection(firestore, 'usersPublic'),
        where('usernameLower', '==', value.conversationStarter.toLowerCase())
      );
      const userResult = await getDocs(userQuery);
      const user = userResult.docs[0].data();
      const userID = userResult.docs[0].id;
      const newConversation = {
        promptTitle: value.promptTitle,
        promptID,
        promptCreator: prompt.creator,
        promptCreatorID: prompt.creatorID,
        conversationStarter: user.username,
        conversationStarterID: userID,
        createdAt: serverTimestamp()
      };
      await addDoc(collection(firestore, 'conversations'), newConversation);
    });
    dispatch(asyncActionFinish());
  } catch (error) {
    console.error(error);
    dispatch(asyncActionError());
  }
};

// reset the conversations in redux state to initialState ({})
export const removeConversationsInState = () => dispatch => {
  dispatch({ type: REMOVE_CONVERSATIONS_REDUX_STATE });
};

// allow an user to "delete" the conversation by removing it's
// username and userId from participants and participantsIDs arrays
// of the conversation doc, also makes either promptCreator or conversationStarter
// null depends on who has deleted the conversation
export const deleteConversation = conversationId => async (
  dispatch,
  getState
) => {
  dispatch(asyncActionStart());
  try {
    const userId = getState().auth.currentUser.uid;
    const username = getState().auth.currentUser.displayName;
    const conversationRef = doc(firestore, 'conversations', conversationId);

    await runTransaction(firestore, async transaction => {
      const conversationDoc = await transaction.get(conversationRef);
      const {
        participants,
        participantsIDs,
        conversationStarterID,
        promptCreatorID
      } = conversationDoc.data();
      const participantsMod = participants.filter(participant => {
        // compare lowercase-to-lowercase: `username` is the original-case
        // displayName, so without lowercasing it too, any user with an
        // uppercase letter is never removed from participants.
        return participant.toLowerCase() !== username.toLowerCase();
      });
      const participantsIDsMod = participantsIDs.filter(participantId => {
        return participantId !== userId;
      });
      if (conversationStarterID === userId) {
        const conversationStarterIDMod = null;
        transaction.update(conversationRef, {
          participants: participantsMod,
          participantsIDs: participantsIDsMod,
          conversationStarterID: conversationStarterIDMod
        });
      } else if (promptCreatorID === userId) {
        const promptCreatorIDMod = null;
        transaction.update(conversationRef, {
          participants: participantsMod,
          participantsIDs: participantsIDsMod,
          promptCreatorID: promptCreatorIDMod
        });
      }
    });
    dispatch(asyncActionFinish());
  } catch (error) {
    console.error(error);
    dispatch(
      openSnackbar('ErrorSnackbar', {
        message: 'Could not delete this conversation. Please try again.'
      })
    );
    dispatch(asyncActionError());
  }
};
